/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {Text, Flex, Link, useMultiStyleConfig, Box, Container} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'
import RecommendedProducts from '../../../components/recommended-products'
import ContentSlot from '../../../components/content-slot'
import BackToTopLink from '../../../components/back-to-top-link'
import {useLocation} from 'react-router-dom'
import Search from '../../../components/search'
import {SearchIcon} from '../../../components/icons'
import {FormattedMessage, useIntl} from 'react-intl'
import FiltersSection from './filters-section'
import {HOME_HREF} from '../../../constants'
import {
    ProductQuickviewModal,
    useProductQuickviewModal
} from '../../../components/product-quickview-modal'

const RecommendedProductsWrapper = ({children}) => {
    const styles = useMultiStyleConfig('EmptyResults')
    return <Box {...styles.recommendedProductsWrapper}>{children}</Box>
}
RecommendedProductsWrapper.propTypes = {
    children: PropTypes.node
}

const RecentlyViewedWrapper = ({children}) => {
    const styles = useMultiStyleConfig('EmptyResults')
    return <Box {...styles.recentlyViewedProductsWrapper}>{children}</Box>
}
RecentlyViewedWrapper.propTypes = {
    children: PropTypes.node
}

const EmptyResults = ({
    isSearch,
    searchQuery,
    category,
    suggestions,
    filtersRef,
    filtersProps,
    hasSelectedRefinements
}) => {
    const styles = useMultiStyleConfig('EmptyResults')
    const location = useLocation()
    const {formatMessage} = useIntl()
    const productQuickviewModalProps = useProductQuickviewModal()
    productQuickviewModalProps.onRecommendedProductClick = productQuickviewModalProps.onOpen

    const filters = hasSelectedRefinements && (
        <Container ref={filtersRef} layerStyle="page" variant="no-padding">
            <FiltersSection
                {...filtersProps}
                resetLabel={formatMessage({
                    id: 'selected_refinements.action.reset',
                    defaultMessage: 'Reset'
                })}
            />
        </Container>
    )

    return (
        <Flex data-testid="sf-product-empty-list-page" {...styles.container}>
            {isSearch && (
                // Top Banner
                <ContentSlot
                    slotId="search-no-results-banner-top"
                    showError={false}
                    variant="cms"
                />
            )}

            <>
                {!isSearch ? (
                    <>
                        {filters}
                        <Container layerStyle="page" variant="no-padding">
                            <Text {...styles.cantFindForCategory}>
                                {hasSelectedRefinements ? (
                                    <FormattedMessage
                                        defaultMessage="Sorry we have no product that fits within the range you have chosen, just click on RESET to see the full product search results again."
                                        id="empty_search_results.info.msg_reset"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="empty_search_results.info.cant_find_anything_for_category"
                                        defaultMessage="We couldn’t find anything for {category}. Try searching for a product or {link}."
                                        values={{
                                            category: category?.name,
                                            link: (
                                                <Link as={RouteLink} to={HOME_HREF}>
                                                    <FormattedMessage
                                                        defaultMessage="Contact Us"
                                                        id="global.link.contact_us"
                                                    />
                                                </Link>
                                            )
                                        }}
                                    />
                                )}
                            </Text>
                        </Container>
                    </>
                ) : (
                    <>
                        <Flex {...styles.searchAndSuggestionsContainer}>
                            <Flex {...styles.searchContainer}>
                                <Search
                                    placeholder={formatMessage({
                                        id: 'global.search',
                                        defaultMessage: 'Search'
                                    })}
                                    variant="flushed"
                                    leftIcon={<SearchIcon boxSize={4} />}
                                    dropdownProps={{
                                        marginLeft: {md: 0},
                                        paddingX: {base: 4, md: 8}
                                    }}
                                />
                            </Flex>
                            {searchQuery && (
                                <Text {...styles.cantFindForQuery}>
                                    <FormattedMessage
                                        defaultMessage={'No search results for "{searchQuery}".'}
                                        id="empty_search_results.info.cant_find_anything_for_query"
                                        values={{searchQuery}}
                                    />
                                </Text>
                            )}
                            <span>
                                {hasSelectedRefinements ? (
                                    <>
                                        {filters}
                                        <Text {...styles.pleaseStartNewSearch}>
                                            <FormattedMessage
                                                defaultMessage="Sorry we have no product that fits within the range you have chosen, just click on RESET to see the full product search results again."
                                                id="empty_search_results.info.msg_reset"
                                            />
                                        </Text>
                                    </>
                                ) : (
                                    <>
                                        <Text {...styles.pleaseStartNewSearch}>
                                            <FormattedMessage
                                                defaultMessage="Please start a new search or use suggestions below."
                                                id="empty_search_results.info.msg"
                                            />
                                        </Text>
                                        {suggestions?.length && (
                                            <Flex {...styles.suggestionsContainer}>
                                                <Text {...styles.didYouMean}>
                                                    <FormattedMessage
                                                        defaultMessage="Did you mean"
                                                        id="empty_search_results.info.did_you_mean"
                                                    />
                                                    <span>:</span>
                                                </Text>
                                                {suggestions.map((suggestion, index) => (
                                                    <Link
                                                        key={index}
                                                        size="sm"
                                                        href={`${location.pathname}?q=${suggestion.phrase}`}
                                                    >
                                                        {suggestion.phrase}
                                                    </Link>
                                                ))}
                                            </Flex>
                                        )}
                                    </>
                                )}
                            </span>
                        </Flex>
                        <Flex {...styles.slotsAndRecommendationContainer}>
                            {/* Middle Banner */}
                            <ContentSlot
                                slotId="search-no-results-banner-middle"
                                showError={false}
                                variant="cms"
                            />

                            {/* Recommended products */}
                            <RecommendedProducts
                                wrapper={RecommendedProductsWrapper}
                                title={
                                    <FormattedMessage
                                        defaultMessage="Top Sellers"
                                        id="empty_search_results.recommended_products.title.top_sellers"
                                    />
                                }
                                recommender={'custom-BestSeller'}
                                onRecommendedProductClick={
                                    productQuickviewModalProps.onRecommendedProductClick
                                }
                            />

                            {/* Recently viewed */}
                            <RecommendedProducts
                                wrapper={RecentlyViewedWrapper}
                                title={
                                    <FormattedMessage
                                        defaultMessage="Recently Viewed"
                                        id="product_detail.recommended_products.title.recently_viewed"
                                    />
                                }
                                recommender={'recently-viewed'}
                                onRecommendedProductClick={
                                    productQuickviewModalProps.onRecommendedProductClick
                                }
                            />

                            {/* Bottom Banner */}
                            <ContentSlot
                                slotId="search-no-results-banner-bottom"
                                showError={false}
                                variant="cms"
                            />
                        </Flex>
                        <BackToTopLink {...styles.backToTopLink} />
                    </>
                )}
            </>
            <ProductQuickviewModal {...productQuickviewModalProps} />
        </Flex>
    )
}

EmptyResults.propTypes = {
    isSearch: PropTypes.bool,
    searchQuery: PropTypes.string,
    category: PropTypes.object,
    suggestions: PropTypes.array,
    filtersRef: PropTypes.object,
    filtersProps: PropTypes.object,
    hasSelectedRefinements: PropTypes.bool
}

export default EmptyResults
